import { ConfigurationServiceImpl } from "~/backend/generated/Configuration/api/configuration.service";
import { PublicConfigResponse } from "~/backend/generated/Configuration/model/publicConfigResponse";
import { AccountSidOption, PublicConfig, RuntimeDomainOption } from "~/modules/config";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import { ErrorCode, ErrorSeverity, FlexSdkErrorMetadata } from "~/modules/error";
import { mapRootKeysToCamelCase } from "~/utils/mapKeys";
import { XOR } from "~/utils/utilityTypes";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

const TWILIO_DOMAIN = ".twil.io";
const RUNTIME_DOMAIN_REGEXP = /^([a-z]+-[a-z]+-\d+)$/;

const metadata: FlexSdkErrorMetadata = {
    module: "config",
    severity: ErrorSeverity.Error,
    source: "getPublicConfig.ts"
};

function sanitizeRuntimeDomain(domain: string): string {
    let sanitizedDomain = domain.trim();

    if (sanitizedDomain.endsWith("/")) {
        sanitizedDomain = sanitizedDomain.replace(/\/$/, "");
    }

    if (!sanitizedDomain.endsWith(TWILIO_DOMAIN)) {
        const parsedRuntimeDomain = RUNTIME_DOMAIN_REGEXP.exec(sanitizedDomain);
        if (parsedRuntimeDomain?.[1]) {
            sanitizedDomain += TWILIO_DOMAIN;
        }
    }

    return sanitizedDomain;
}

function isAccountSidOption(option: XOR<AccountSidOption, RuntimeDomainOption>): option is AccountSidOption {
    return !!(option as AccountSidOption).accountSid;
}

function getPublicConfigQueryParam(option: XOR<AccountSidOption, RuntimeDomainOption>): {
    paramName: "AccountSid" | "RuntimeDomain";
    paramValue: string;
} {
    if (isAccountSidOption(option)) {
        const accountSid = (option as AccountSidOption).accountSid;
        return { paramName: "AccountSid", paramValue: accountSid };
    }

    const runtimeDomain = (option as RuntimeDomainOption).runtimeDomain;
    const sanitizedDomain = sanitizeRuntimeDomain(runtimeDomain);
    return { paramName: "RuntimeDomain", paramValue: sanitizedDomain };
}

export const getPublicConfig =
    (ctx: ContextManager) =>
    async (option: XOR<AccountSidOption, RuntimeDomainOption>): Promise<PublicConfig> => {
        const configurationService = new ConfigurationServiceImpl(ctx);
        const queryParam = getPublicConfigQueryParam(option);
        let responseData: PublicConfigResponse;

        try {
            responseData = await configurationService.fetchPublicConfiguration(
                queryParam.paramName,
                queryParam.paramValue
            );
        } catch (error) {
            const errorMsg = `getPublicConfig: Failed to fetch public configuration for ${queryParam?.paramName}=${queryParam?.paramValue}`;
            return throwFlexSdkError(ctx)(ErrorCode.FailedToFetchPublicConfiguration, metadata, errorMsg, error);
        }
        if (!responseData?.configurations?.length) {
            throwFlexSdkError(ctx)(
                ErrorCode.InvalidResponseFromPublicConfig,
                { ...metadata, severity: ErrorSeverity.Error },
                "getPublicConfig: Invalid response from public configuration endpoint"
            );
        }
        return mapRootKeysToCamelCase(responseData.configurations[0]) as PublicConfig;
    };
